import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import PlainDateMonthSwitcher from 'app/common/ui-next/plain-date-month-picker'
import PlainDateSwitcher from 'app/common/ui-next/plain-date-switcher'
import useHeaderByDateI18n from 'app/common/hooks/useHeaderByDateI18n'
import useFromTo from 'app/common/hooks/useFromTo'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import * as dateTypes from 'app/common/constants/dateTypes'
import SimpleDate from 'app/common/classes/simple-date'

import StatusFilter from '../commonComponents/StatusFilter'
import { PurchasesApi } from '../PurchasesApi'
import OrdersTable from './OrdersTable'
import { status2TranslationMap, status2ColorMap } from './statusProps'
import { status2apiMap } from '../dataTypes/orders/status2api'
import { statuses } from '../dataTypes/orders/statuses'

import styles from './CafeteriaOrders.module.scss';

/**
 *   Компонент столовой Заказы
 */

const cx = cn.bind(styles)

type Props = {
  date: SimpleDate
  dateType: 'month' | 'day'
  person: {
    id: number
    name: {
      first: string
      last: string
      middle: string
      gender: 'male' | 'female' | 'androgynous'
    }
    timezone: string
  }
  onChangeDate: (date: string, dateFormat: string, daily?: boolean) => void
}

const CafeteriaOrders = ({
  date,
  dateType: dt,
  person: { id: personId, name: personName, timezone },
  onChangeDate,
}: Props) => {
  const { t } = useTranslation('cafeteria')

  const dateType = dt === 'day' ? dateTypes.DAYS : dateTypes.MONTHS // временно, до полной замены

  const dateString = date.getStringFormatted(dt === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM')
  const [from, to] = useFromTo({ date: dateString, dateType })

  const header = useHeaderByDateI18n({
    date: dateString,
    dateType,
    name: personName,
    ns: 'cafeteria',
    i18nId: 'orders_header',
  })

  const [currentStatus, setCurrentStatus] = useState<string>(statuses.All)

  return (
    <PurchasesApi personId={personId} from={from} to={to} autoRequest>
      <Sticky>
        {(ref, sticky) => (
          <PageTemplateContent reference={ref} className={cx({ sticky })} flex>
            <Header2 skipMargin>{header}</Header2>
            <PlainDateSwitcher
              date={dateString}
              dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
              displayDateFormat={dateType === dateTypes.MONTHS ? 'MMMM' : undefined}
              stepType={dateType === dateTypes.MONTHS ? 'month' : 'day'}
              onDateChange={onChangeDate}
            >
              <PlainDateMonthSwitcher
                date={dateString}
                dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
                onChangeDate={onChangeDate}
                monthsAmount={24}
              />
            </PlainDateSwitcher>
          </PageTemplateContent>
        )}
      </Sticky>
      <Sticky>
        {(ref) => (
          <PageTemplateContent reference={ref} flex>
            <StatusFilter
              currentStatus={currentStatus}
              onClickItem={setCurrentStatus}
              status2ColorMap={status2ColorMap}
              status2TranslationMap={status2TranslationMap}
              title={t('orders_filter_title', 'Показать')}
              onTranslate={t}
            />
          </PageTemplateContent>
        )}
      </Sticky>
      <OrdersTable timezone={timezone} statusFilter={status2apiMap.get(currentStatus)} />
    </PurchasesApi>
  )
}

export default CafeteriaOrders
